import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useData } from '../contexts/DataContext';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Shield, Plus, Edit, Trash2, Download, CheckCircle,
  AlertTriangle, XCircle, Clock, Search, Calendar as CalendarIcon, Building2, FileText,
  Bell, Settings, Send, List
} from 'lucide-react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { ro } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';

const iscirLocalizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { locale: ro }),
  getDay,
  locales: { 'ro': ro }
});

const ISCIRPage = () => {
  const { plants } = useData();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [iscirTypes, setIscirTypes] = useState({});
  const [iscirCategories, setIscirCategories] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [filterPlant, setFilterPlant] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('list'); // list / calendar
  const [showAlertSettings, setShowAlertSettings] = useState(false);
  const [alertSettings, setAlertSettings] = useState({ enabled: false, send_email: true, send_sms: true, alert_days: [90, 60, 30, 7], min_interval_hours: 24, recipients: [] });

  const emptyForm = {
    equipment_id: '', iscir_type: '', iscir_category: '',
    certificate_number: '', inspector_name: '', inspector_company: '',
    issue_date: '', expiry_date: '', next_verification_date: '',
    rsvti_name: '', rsvti_type: 'intern', notes: '',
    alert_days: [90, 60, 30, 7]
  };
  const [form, setForm] = useState(emptyForm);
  const [equipSearch, setEquipSearch] = useState('');
  const [showEquipDropdown, setShowEquipDropdown] = useState(false);
  const equipDropdownRef = useRef(null);

  // Close equipment dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (equipDropdownRef.current && !equipDropdownRef.current.contains(e.target)) {
        setShowEquipDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [rRes, sRes, tRes, iscirEqRes, aRes] = await Promise.all([
        api.get('/iscir/records', { params: { plant_id: filterPlant !== 'all' ? filterPlant : undefined, iscir_type: filterType !== 'all' ? filterType : undefined, status: filterStatus !== 'all' ? filterStatus : undefined } }),
        api.get('/iscir/stats', { params: { plant_id: filterPlant !== 'all' ? filterPlant : undefined } }),
        api.get('/iscir/types'),
        api.get('/iscir-equipment'),
        api.get('/iscir/alert-settings').catch(() => ({ data: { enabled: false, recipients: [] } }))
      ]);
      setRecords(rRes.data);
      setStats(sRes.data);
      setIscirTypes(tRes.data.types || {});
      setIscirCategories(tRes.data.categories || []);
      setEquipment(iscirEqRes.data || []);
      setAlertSettings(aRes.data);
    } catch (err) {
      toast.error('Eroare la incarcarea datelor');
    } finally {
      setLoading(false);
    }
  }, [filterPlant, filterType, filterStatus]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async () => {
    if (!form.equipment_id || !form.iscir_type || !form.issue_date || !form.expiry_date) {
      toast.error('Completati campurile obligatorii');
      return;
    }
    try {
      if (editItem) {
        await api.put(`/iscir/records/${editItem.id}`, form);
        toast.success('Inregistrare ISCIR actualizata!');
      } else {
        await api.post('/iscir/records', form);
        toast.success('Inregistrare ISCIR creata!');
      }
      setShowDialog(false);
      setEditItem(null);
      setForm(emptyForm);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eroare la salvare');
    }
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setForm({
      equipment_id: item.equipment_id || '',
      iscir_type: item.iscir_type || '',
      iscir_category: item.iscir_category || '',
      certificate_number: item.certificate_number || '',
      inspector_name: item.inspector_name || '',
      inspector_company: item.inspector_company || '',
      issue_date: item.issue_date || '',
      expiry_date: item.expiry_date || '',
      next_verification_date: item.next_verification_date || '',
      rsvti_name: item.rsvti_name || '',
      rsvti_type: item.rsvti_type || 'intern',
      notes: item.notes || '',
      alert_days: item.alert_days || [90, 60, 30, 7]
    });
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sigur doriti sa stergeti?')) return;
    try {
      await api.delete(`/iscir/records/${id}`);
      toast.success('Sters!');
      fetchData();
    } catch (err) { toast.error('Eroare'); }
  };

  const handleDownloadDossier = async (eqId) => {
    try {
      const res = await api.get(`/iscir/equipment/${eqId}/dossier-pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `dosar_iscir_${eqId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Dosar ISCIR descarcat');
    } catch (err) { toast.error('Eroare la descarcare'); }
  };

  const handleDownloadReport = async () => {
    try {
      const res = await api.get('/iscir/compliance-report-pdf', { params: { plant_id: filterPlant !== 'all' ? filterPlant : undefined }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `raport_conformitate_iscir.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Raport conformitate descarcat');
    } catch (err) { toast.error('Eroare la descarcare'); }
  };

  const handleSaveAlertSettings = async () => {
    try {
      await api.post('/iscir/alert-settings', alertSettings);
      toast.success('Setari alerte ISCIR salvate!');
      setShowAlertSettings(false);
    } catch (err) { toast.error('Eroare la salvarea setarilor'); }
  };

  const addAlertRecipient = () => {
    setAlertSettings({
      ...alertSettings,
      recipients: [...alertSettings.recipients, { name: '', email: '', phone: '', notify_email: true, notify_sms: true }]
    });
  };

  const updateAlertRecipient = (idx, field, val) => {
    const r = [...alertSettings.recipients];
    r[idx] = { ...r[idx], [field]: val };
    setAlertSettings({ ...alertSettings, recipients: r });
  };

  const removeAlertRecipient = (idx) => {
    setAlertSettings({ ...alertSettings, recipients: alertSettings.recipients.filter((_, i) => i !== idx) });
  };

  const handleTestAlert = async () => {
    try {
      const res = await api.post('/iscir/test-alert');
      toast.success(`Verificare executata: ${res.data.result?.sent || 0} alerte`);
    } catch (err) { toast.error('Eroare la testare'); }
  };

  const statusIcon = (s) => {
    if (s === 'valid') return <CheckCircle className="w-4 h-4 text-emerald-500" />;
    if (s === 'attention' || s === 'expiring_soon') return <AlertTriangle className="w-4 h-4 text-amber-500" />;
    if (s === 'expired') return <XCircle className="w-4 h-4 text-red-500" />;
    return <Clock className="w-4 h-4 text-slate-400" />;
  };

  const statusBadge = (s, daysLeft) => {
    if (s === 'valid') return <Badge className="bg-emerald-100 text-emerald-700 text-xs"><CheckCircle className="w-3 h-3 mr-1" /> Valid ({daysLeft}z)</Badge>;
    if (s === 'attention') return <Badge className="bg-amber-100 text-amber-700 text-xs"><AlertTriangle className="w-3 h-3 mr-1" /> Atentie ({daysLeft}z)</Badge>;
    if (s === 'expiring_soon') return <Badge className="bg-orange-100 text-orange-700 text-xs"><AlertTriangle className="w-3 h-3 mr-1" /> Expira curand ({daysLeft}z)</Badge>;
    if (s === 'expired') return <Badge variant="destructive" className="text-xs"><XCircle className="w-3 h-3 mr-1" /> EXPIRAT ({Math.abs(daysLeft)}z)</Badge>;
    return <Badge variant="secondary" className="text-xs">N/A</Badge>;
  };

  const filtered = records.filter(r => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (r.equipment_name || '').toLowerCase().includes(s) ||
           (r.equipment_code || '').toLowerCase().includes(s) ||
           (r.certificate_number || '').toLowerCase().includes(s);
  });

  // Build display label for ISCIR equipment: ISCIR-{cod_intern} - {denumire} or ISCIR - {denumire}
  // ISCIR - {cod_alocare_intern} - {denumire}
  const getEquipLabel = (eq) => {
    const cod = eq.cod_alocare_intern || '';
    const den = eq.denumire || eq.name || '';
    return cod ? `ISCIR - ${cod} - ${den}` : `ISCIR - ${den}`;
  };

  // Filter equipment by search
  const filteredEquipment = equipment.filter(e => {
    if (!equipSearch) return true;
    const s = equipSearch.toLowerCase();
    const label = getEquipLabel(e).toLowerCase();
    return label.includes(s) || (e.serie_utilaj || '').toLowerCase().includes(s) || (e.code || '').toLowerCase().includes(s);
  });

  // Get selected equipment label
  const selectedEquipLabel = form.equipment_id ? getEquipLabel(equipment.find(e => e.id === form.equipment_id) || {}) : '';

  return (
    <div className="space-y-6" data-testid="iscir-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl lg:text-4xl font-bold text-slate-900">Conformitate ISCIR</h1>
          <p className="text-slate-600 mt-1">Certificari si verificari echipamente ({records.length} inregistrari)</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => setShowAlertSettings(true)} data-testid="alert-settings-btn">
            <Bell className="w-4 h-4 mr-2" /> Alerte
            {alertSettings.enabled && <span className="ml-1 w-2 h-2 bg-emerald-500 rounded-full inline-block" />}
          </Button>
          <Button variant="outline" onClick={handleDownloadReport} data-testid="download-report-btn">
            <Download className="w-4 h-4 mr-2" /> Raport PDF
          </Button>
          <Button className="btn-primary" onClick={() => { setEditItem(null); setForm(emptyForm); setShowDialog(true); }} data-testid="add-iscir-btn">
            <Plus className="w-4 h-4 mr-2" /> ADAUGA VERIFICARE
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Card className="border-0 shadow-sm"><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-slate-900">{stats.total || 0}</p><p className="text-xs text-slate-500">Total</p>
        </CardContent></Card>
        <Card className="border-0 shadow-sm border-l-4 border-l-emerald-500"><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-emerald-600">{stats.valid || 0}</p><p className="text-xs text-slate-500">Valide</p>
        </CardContent></Card>
        <Card className="border-0 shadow-sm border-l-4 border-l-amber-500"><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-amber-600">{stats.attention || 0}</p><p className="text-xs text-slate-500">Atentie (&lt;90z)</p>
        </CardContent></Card>
        <Card className="border-0 shadow-sm border-l-4 border-l-orange-500"><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-orange-600">{stats.expiring_soon || 0}</p><p className="text-xs text-slate-500">Expira curand (&lt;30z)</p>
        </CardContent></Card>
        <Card className="border-0 shadow-sm border-l-4 border-l-red-500"><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-red-600">{stats.expired || 0}</p><p className="text-xs text-slate-500">Expirate</p>
        </CardContent></Card>
      </div>

      {/* Tabs: List / Calendar */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-slate-100 p-1">
          <TabsTrigger value="list" data-testid="iscir-tab-list"><List className="w-4 h-4 mr-1" /> Lista</TabsTrigger>
          <TabsTrigger value="calendar" data-testid="iscir-tab-calendar"><CalendarIcon className="w-4 h-4 mr-1" /> Calendar</TabsTrigger>
        </TabsList>

        {/* List Tab */}
        <TabsContent value="list">
          {/* Filters */}
          <div className="flex flex-wrap gap-3 items-center mb-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Cauta echipament, cod, certificat..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-10" data-testid="iscir-search" />
            </div>
            <Select value={filterPlant} onValueChange={setFilterPlant}>
              <SelectTrigger className="w-40 h-10"><Building2 className="w-4 h-4 mr-1" /><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toate fabricile</SelectItem>
                {plants?.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-36 h-10"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toate statusurile</SelectItem>
                <SelectItem value="valid">Valid</SelectItem>
                <SelectItem value="attention">Atentie</SelectItem>
                <SelectItem value="expiring_soon">Expira curand</SelectItem>
                <SelectItem value="expired">Expirat</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-48 h-10"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toate tipurile</SelectItem>
                {Object.entries(iscirTypes).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Records List */}
          {loading ? (
            <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#0052CC]" /></div>
          ) : (
            <Card className="border-0 shadow-sm">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-left">
                      <tr>
                        <th className="p-3 font-medium text-slate-600">Echipament</th>
                        <th className="p-3 font-medium text-slate-600">Tip Verificare</th>
                        <th className="p-3 font-medium text-slate-600">Nr. Certificat</th>
                        <th className="p-3 font-medium text-slate-600">Emitere</th>
                        <th className="p-3 font-medium text-slate-600">Expirare</th>
                        <th className="p-3 font-medium text-slate-600">Inspector</th>
                        <th className="p-3 font-medium text-slate-600">RSVTI</th>
                        <th className="p-3 font-medium text-slate-600">Status</th>
                        <th className="p-3 font-medium text-slate-600 text-right">Actiuni</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {filtered.length === 0 && (
                        <tr><td colSpan={9} className="p-12 text-center text-slate-500">
                          <Shield className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                          <p>Nicio inregistrare ISCIR</p>
                        </td></tr>
                      )}
                      {filtered.map(r => (
                        <tr key={r.id} className={`hover:bg-slate-50 ${r.status === 'expired' ? 'bg-red-50/50' : r.status === 'expiring_soon' ? 'bg-amber-50/50' : ''}`}>
                          <td className="p-3">
                            <div>
                              <p className="font-medium text-slate-900">{r.equipment_name}</p>
                              <p className="text-xs text-slate-500 font-mono">{r.equipment_code}</p>
                            </div>
                          </td>
                          <td className="p-3 text-xs">{iscirTypes[r.iscir_type] || r.iscir_type}</td>
                          <td className="p-3 font-mono text-xs">{r.certificate_number || '-'}</td>
                          <td className="p-3 text-xs">{r.issue_date}</td>
                          <td className="p-3 text-xs font-medium">{r.expiry_date}</td>
                          <td className="p-3 text-xs">{r.inspector_name || '-'}</td>
                          <td className="p-3 text-xs">{r.rsvti_name ? `${r.rsvti_name} (${r.rsvti_type})` : '-'}</td>
                          <td className="p-3">{statusBadge(r.status, r.days_left)}</td>
                          <td className="p-3">
                            <div className="flex items-center justify-end gap-1">
                              <Button variant="ghost" size="sm" onClick={() => handleDownloadDossier(r.equipment_id)} title="Dosar conformitate PDF"><Download className="w-4 h-4 text-blue-600" /></Button>
                              <Button variant="ghost" size="sm" onClick={() => handleEdit(r)}><Edit className="w-4 h-4" /></Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDelete(r.id)} className="text-red-600"><Trash2 className="w-4 h-4" /></Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Calendar Tab */}
        <TabsContent value="calendar">
          <Card>
            <CardContent className="p-4">
              <div style={{ height: 600 }} data-testid="iscir-calendar">
                <Calendar
                  localizer={iscirLocalizer}
                  events={records.map(r => ({
                    id: r.id,
                    title: `${r.equipment_name} - ${iscirTypes[r.iscir_type] || r.iscir_type}`,
                    start: new Date(r.expiry_date),
                    end: new Date(r.expiry_date),
                    allDay: true,
                    resource: r
                  }))}
                  startAccessor="start"
                  endAccessor="end"
                  defaultView="month"
                  views={['month', 'agenda']}
                  culture="ro"
                  messages={{
                    today: 'Azi',
                    previous: 'Anterior',
                    next: 'Urmator',
                    month: 'Luna',
                    week: 'Saptamana',
                    day: 'Zi',
                    agenda: 'Agenda',
                    noEventsInRange: 'Nicio verificare ISCIR in aceasta perioada'
                  }}
                  eventPropGetter={(event) => {
                    const r = event.resource;
                    let bg = '#36B37E';
                    if (r.status === 'expired') bg = '#EF4444';
                    else if (r.status === 'expiring_soon') bg = '#F97316';
                    else if (r.status === 'attention') bg = '#F59E0B';
                    return { style: { backgroundColor: bg, color: '#fff', border: 'none', borderRadius: '4px', fontSize: '11px', padding: '2px 6px' } };
                  }}
                  onSelectEvent={(event) => {
                    const r = event.resource;
                    if (r) handleEdit(r);
                  }}
                />
              </div>
              <div className="flex gap-4 mt-4 justify-center">
                <div className="flex items-center gap-2 text-xs"><div className="w-3 h-3 rounded bg-[#36B37E]" /> Valid</div>
                <div className="flex items-center gap-2 text-xs"><div className="w-3 h-3 rounded bg-[#F59E0B]" /> Atentie</div>
                <div className="flex items-center gap-2 text-xs"><div className="w-3 h-3 rounded bg-[#F97316]" /> Expira curand</div>
                <div className="flex items-center gap-2 text-xs"><div className="w-3 h-3 rounded bg-[#EF4444]" /> Expirat</div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={o => { setShowDialog(o); if (!o) { setEditItem(null); setForm(emptyForm); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editItem ? 'Editeaza Verificare ISCIR' : 'Adauga Verificare ISCIR'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Echipament * <span className="text-xs text-slate-400 font-normal">(Nomenclator ISCIR)</span></Label>
                <div className="relative" data-testid="equip-search-wrapper" ref={equipDropdownRef}>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Cauta echipament ISCIR..."
                      value={showEquipDropdown ? equipSearch : selectedEquipLabel}
                      onChange={e => { setEquipSearch(e.target.value); setShowEquipDropdown(true); }}
                      onFocus={() => setShowEquipDropdown(true)}
                      className="h-11 pl-9 text-sm"
                      data-testid="equip-search-input"
                    />
                    {form.equipment_id && !showEquipDropdown && (
                      <button className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" onClick={() => { setForm({ ...form, equipment_id: '' }); setEquipSearch(''); }}>
                        <XCircle className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  {showEquipDropdown && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                      {filteredEquipment.length === 0 ? (
                        <div className="p-3 text-sm text-slate-500 text-center">Niciun echipament gasit</div>
                      ) : (
                        filteredEquipment.map(e => (
                          <button
                            key={e.id}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 transition-colors border-b border-slate-50 last:border-0 ${form.equipment_id === e.id ? 'bg-blue-50 text-blue-700 font-medium' : 'text-slate-700'}`}
                            onClick={() => { 
                              const cat = e.iscir_category || '';
                              setForm({ ...form, equipment_id: e.id, iscir_category: cat || form.iscir_category }); 
                              setShowEquipDropdown(false); 
                              setEquipSearch(''); 
                            }}
                            data-testid={`equip-option-${e.code}`}
                          >
                            {getEquipLabel(e)}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label>Tip Verificare ISCIR *</Label>
                <Select value={form.iscir_type} onValueChange={v => setForm({ ...form, iscir_type: v })}>
                  <SelectTrigger className="h-11"><SelectValue placeholder="Selecteaza" /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(iscirTypes).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Categorie ISCIR Echipament</Label>
                <Select value={form.iscir_category} onValueChange={v => setForm({ ...form, iscir_category: v })}>
                  <SelectTrigger className="h-11"><SelectValue placeholder="Selecteaza" /></SelectTrigger>
                  <SelectContent>
                    {iscirCategories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Nr. Certificat / PV</Label>
                <Input value={form.certificate_number} onChange={e => setForm({ ...form, certificate_number: e.target.value })} className="h-11" placeholder="Ex: CERT-2024-001" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Data emitere *</Label>
                <Input type="date" value={form.issue_date} onChange={e => setForm({ ...form, issue_date: e.target.value })} className="h-11" />
              </div>
              <div className="space-y-2">
                <Label>Data expirare *</Label>
                <Input type="date" value={form.expiry_date} onChange={e => setForm({ ...form, expiry_date: e.target.value })} className="h-11" />
              </div>
              <div className="space-y-2">
                <Label>Urmatoarea verificare</Label>
                <Input type="date" value={form.next_verification_date} onChange={e => setForm({ ...form, next_verification_date: e.target.value })} className="h-11" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Inspector ISCIR</Label>
                <Input value={form.inspector_name} onChange={e => setForm({ ...form, inspector_name: e.target.value })} className="h-11" placeholder="Numele inspectorului" />
              </div>
              <div className="space-y-2">
                <Label>Companie Inspector</Label>
                <Input value={form.inspector_company} onChange={e => setForm({ ...form, inspector_company: e.target.value })} className="h-11" placeholder="Ex: ISCIR, CNCIR" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>RSVTI Responsabil</Label>
                <Input value={form.rsvti_name} onChange={e => setForm({ ...form, rsvti_name: e.target.value })} className="h-11" placeholder="Nume RSVTI" />
              </div>
              <div className="space-y-2">
                <Label>Tip RSVTI</Label>
                <Select value={form.rsvti_type} onValueChange={v => setForm({ ...form, rsvti_type: v })}>
                  <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="intern">Intern</SelectItem>
                    <SelectItem value="extern">Extern</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Observatii</Label>
              <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} placeholder="Note suplimentare..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Anuleaza</Button>
            <Button className="btn-primary" onClick={handleSubmit} data-testid="save-iscir-btn">Salveaza</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Alert Settings Dialog */}
      <Dialog open={showAlertSettings} onOpenChange={setShowAlertSettings}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Bell className="w-5 h-5" /> Setari Alerte ISCIR</DialogTitle></DialogHeader>
          <div className="space-y-5">
            {/* Enable/Disable */}
            <div className="flex items-center justify-between bg-slate-50 p-4 rounded-lg">
              <div>
                <p className="font-medium">Alerte automate active</p>
                <p className="text-sm text-slate-500">Verificare zilnica a scadentelor ISCIR</p>
              </div>
              <button
                onClick={() => setAlertSettings({ ...alertSettings, enabled: !alertSettings.enabled })}
                className={`w-12 h-7 rounded-full transition-colors relative ${alertSettings.enabled ? 'bg-emerald-500' : 'bg-slate-300'}`}
                data-testid="alert-toggle"
              >
                <span className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${alertSettings.enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </button>
            </div>

            {/* Channels */}
            <div className="grid grid-cols-2 gap-4">
              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-slate-50">
                <input type="checkbox" checked={alertSettings.send_email} onChange={e => setAlertSettings({ ...alertSettings, send_email: e.target.checked })} className="w-4 h-4 rounded" />
                <div><p className="font-medium text-sm">Email</p><p className="text-xs text-slate-500">Trimite alerte pe email</p></div>
              </label>
              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-slate-50">
                <input type="checkbox" checked={alertSettings.send_sms} onChange={e => setAlertSettings({ ...alertSettings, send_sms: e.target.checked })} className="w-4 h-4 rounded" />
                <div><p className="font-medium text-sm">SMS</p><p className="text-xs text-slate-500">Trimite alerte prin SMS</p></div>
              </label>
            </div>

            {/* Alert Days */}
            <div className="space-y-2">
              <Label>Intervale alerta (zile inainte de expirare)</Label>
              <div className="flex gap-2 flex-wrap">
                {[90, 60, 30, 14, 7, 3, 1].map(d => (
                  <button key={d}
                    onClick={() => {
                      const days = alertSettings.alert_days || [];
                      setAlertSettings({ ...alertSettings, alert_days: days.includes(d) ? days.filter(x => x !== d) : [...days, d].sort((a, b) => b - a) });
                    }}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${(alertSettings.alert_days || []).includes(d) ? 'bg-blue-100 border-blue-300 text-blue-700' : 'bg-white border-slate-200 text-slate-500'}`}
                  >{d} zile</button>
                ))}
              </div>
            </div>

            {/* Min interval between alerts */}
            <div className="space-y-2">
              <Label>Interval minim intre alerte</Label>
              <p className="text-xs text-slate-500">Previne trimiterea multipla a alertelor la intervale scurte (ex: la repornirea serverului)</p>
              <div className="flex gap-2 flex-wrap">
                {[6, 12, 24, 48].map(h => (
                  <button key={h}
                    onClick={() => setAlertSettings({ ...alertSettings, min_interval_hours: h })}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${(alertSettings.min_interval_hours || 24) === h ? 'bg-emerald-100 border-emerald-300 text-emerald-700' : 'bg-white border-slate-200 text-slate-500'}`}
                    data-testid={`interval-${h}h-btn`}
                  >{h} ore</button>
                ))}
              </div>
            </div>

            {/* Recipients */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Destinatari alerte</Label>
                <Button type="button" variant="outline" size="sm" onClick={addAlertRecipient} data-testid="add-recipient-btn">
                  <Plus className="w-3 h-3 mr-1" /> Adauga
                </Button>
              </div>
              {(alertSettings.recipients || []).length === 0 && (
                <p className="text-sm text-slate-500 text-center py-4">Niciun destinatar configurat. Adaugati cel putin un destinatar.</p>
              )}
              {(alertSettings.recipients || []).map((r, idx) => (
                <div key={idx} className="border rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-600">Destinatar {idx + 1}</span>
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeAlertRecipient(idx)} className="text-red-500 h-7"><Trash2 className="w-3 h-3" /></Button>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <Input placeholder="Nume" value={r.name} onChange={e => updateAlertRecipient(idx, 'name', e.target.value)} className="h-9 text-sm" />
                    <Input placeholder="Email" type="email" value={r.email} onChange={e => updateAlertRecipient(idx, 'email', e.target.value)} className="h-9 text-sm" />
                    <Input placeholder="Telefon" value={r.phone} onChange={e => updateAlertRecipient(idx, 'phone', e.target.value)} className="h-9 text-sm" />
                  </div>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={r.notify_email} onChange={e => updateAlertRecipient(idx, 'notify_email', e.target.checked)} className="w-3.5 h-3.5 rounded" /> Email
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={r.notify_sms} onChange={e => updateAlertRecipient(idx, 'notify_sms', e.target.checked)} className="w-3.5 h-3.5 rounded" /> SMS
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter className="flex justify-between">
            <Button variant="outline" onClick={handleTestAlert} data-testid="test-alert-btn">
              <Send className="w-4 h-4 mr-2" /> Testeaza Acum
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowAlertSettings(false)}>Anuleaza</Button>
              <Button className="btn-primary" onClick={handleSaveAlertSettings} data-testid="save-alert-settings-btn">Salveaza Setari</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ISCIRPage;
